import { useState, useCallback } from 'react'
import { useTablesQuery } from '@/hooks/useDatabase'
import { useSettingsStore } from '@/stores/settingsStore'
import type { DatabaseConnection } from '@/stores/connectionStore'
import type { AgentProxyGetTablesResponse } from '@/services/api/models/AgentProxyGetTablesResponse'
import {
  TableCellsIcon,
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

// Define Table type from the response structure with proper typing
type Table = NonNullable<AgentProxyGetTablesResponse['tables']>[0]

// Type guard to ensure we have a valid table with required properties
const isValidTable = (table: unknown): table is Table => {
  return table !== null && 
         typeof table === 'object' && 
         'name' in table &&
         typeof (table as Record<string, unknown>).name === 'string'
}

interface TablesViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onTableSelect?: (tableName: string) => void
}

export function TablesView({ connection, database, schema, onTableSelect }: TablesViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'rows' | 'size'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [filterType, setFilterType] = useState<'all' | 'base' | 'system'>('all')
  
  const { settings } = useSettingsStore()
  const isCompactView = settings.explorerCompactView

  const { data: tablesResponse, isLoading, error } = useTablesQuery(
    connection.id,
    database,
    schema,
    { 
      enabled: true, 
      limit: 1000, // Fetch more tables for comprehensive view
      databaseType: connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL'
    }
  )

  // Filter tables (exclude views and system tables by default, but allow them via filter)
  const allTables = Array.isArray(tablesResponse?.data) ? tablesResponse.data.filter(isValidTable) : []
  
  // Helper function to parse size strings to bytes for sorting
  const parseSizeToBytes = (sizeStr?: string): number => {
    if (!sizeStr) return 0
    
    // Handle size strings like "0.02 MB", "1.5 GB", etc.
    const match = /^([\d.]+)\s*(B|KB|MB|GB|TB)$/i.exec(sizeStr)
    if (!match) return 0
    
    const value = Number.parseFloat(match[1])
    const unit = match[2].toUpperCase()
    
    const multipliers: Record<string, number> = {
      'B': 1,
      'KB': 1024,
      'MB': 1024 * 1024,
      'GB': 1024 * 1024 * 1024,
      'TB': 1024 * 1024 * 1024 * 1024
    }
    
    return value * (multipliers[unit] || 0)
  }

  const tables = allTables.filter((table: Table) => {
    const viewTypes = [
      'VIEW', 'view', 'View',
      'MATERIALIZED VIEW', 'materialized view', 'MaterializedView'
    ]
    
    const systemTableTypes = [
      'SYSTEM TABLE', 'SYSTEM_TABLE', 'system_table', 'SystemTable',
      'SYSTEM TABLE', 'system table'
    ]
    
    const isView = viewTypes.includes(table.type || '')
    const isSystemTable = systemTableTypes.includes(table.type || '')
    
    // Include based on filter
    switch (filterType) {
      case 'all':
        return !isView // Exclude views, include everything else
      case 'base':
        return !isView && !isSystemTable // Only regular tables
      case 'system':
        return isSystemTable // Only system tables
      default:
        return !isView
    }
  })

  // Filter and sort tables
  const filteredAndSortedTables = tables
    .filter((table: Table) => {
      if (!searchTerm) return true
      return table.name.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: Table, b: Table) => {
      let aValue: string | number = ''
      let bValue: string | number = ''

      switch (sortBy) {
        case 'name':
          aValue = a.name || ''
          bValue = b.name || ''
          break
        case 'rows':
          aValue = a.rows || 0
          bValue = b.rows || 0
          break
        case 'size':
          aValue = parseSizeToBytes(a.size) || 0
          bValue = parseSizeToBytes(b.size) || 0
          break
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
      }

      const comparison = String(aValue).localeCompare(String(bValue))
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = useCallback((field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }, [sortBy, sortOrder])

  const handleSortName = useCallback(() => { handleSort('name') }, [handleSort])
  const handleSortRows = useCallback(() => { handleSort('rows') }, [handleSort])
  const handleSortSize = useCallback(() => { handleSort('size') }, [handleSort])
  
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])
  
  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value
    if (v === 'all' || v === 'base' || v === 'system') {
      setFilterType(v)
    }
  }, [])

  const getSortIcon = (field: typeof sortBy) => {
    if (sortBy !== field) {
      return <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" />
    }
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  const formatSize = (sizeBytes?: number) => {
    if (!sizeBytes) return 'N/A'
    
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = sizeBytes
    let unitIndex = 0
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    
    return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`
  }

  const formatNumber = (num?: number | null) => {
    if (num === undefined || num === null) return 'N/A'
    return num.toLocaleString()
  }

  const getTableIcon = (table: Table) => {
    if (table.type.toLowerCase().includes('system')) {
      return <div className="w-4 h-4 flex items-center justify-center text-orange-600 font-bold text-xs">S</div>
    }
    return <TableCellsIcon className="w-4 h-4 text-green-600" />
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading tables: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <TableCellsIcon className="w-6 h-6 mr-2 text-green-600" />
              Tables
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedTables.length} tables
            </p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tables by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={filterType}
              onChange={handleFilterChange}
            >
              <option value="all">All Tables</option>
              <option value="base">User Tables</option>
              <option value="system">System Tables</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tables Table */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          {/* Table Header */}
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className={`grid gap-4 px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
              isCompactView ? 'grid-cols-6' : 'grid-cols-9'
            }`}>
              <div 
                className={`${isCompactView ? 'col-span-4' : 'col-span-4'} flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200`}
                onClick={handleSortName}
              >
                Table Name
                {getSortIcon('name')}
              </div>
              <div className="col-span-1">Engine</div>
              {!isCompactView && (
                <>
                  <div 
                    className="col-span-2 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={handleSortRows}
                  >
                    Rows
                    {getSortIcon('rows')}
                  </div>
                  <div 
                    className="col-span-1 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={handleSortSize}
                  >
                    Size
                    {getSortIcon('size')}
                  </div>
                </>
              )}
              <div className="col-span-1">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedTables.length === 0 ? (
              <div className="text-center py-12">
                <TableCellsIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm || filterType !== 'all' ? 'No tables match your criteria' : 'No tables found'}
                </p>
              </div>
            ) : (
              filteredAndSortedTables.map((table: Table) => (
                <div key={table.name} className={`grid gap-4 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  isCompactView 
                    ? 'grid-cols-6 py-1.5' // Slightly reduced height in compact view 
                    : 'grid-cols-9 py-2'
                }`}>
                  <div className="col-span-4 flex items-center">
                    {getTableIcon(table)}
                    <div className="ml-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {table.name}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-1 flex items-center">
                    <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-600 text-blue-700 dark:text-blue-300 rounded text-xs">
                      {table.engine || 'N/A'}
                    </span>
                  </div>

                  {!isCompactView && (
                    <>
                      <div className="col-span-2 flex items-center">
                        <span className="text-gray-700 dark:text-gray-300 text-sm">
                          {formatNumber(table.rows)}
                        </span>
                      </div>

                      <div className="col-span-1 flex items-center">
                        <span className="text-gray-700 dark:text-gray-300 text-sm">
                          {table.size || 'N/A'}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="col-span-1 flex items-center">
                    <button type="button"
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      title="View table data"
                      onClick={() => onTableSelect?.(table.name)}
                    >
                      <EyeIcon className="w-3 h-3 text-gray-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedTables.length} of {tables.length} tables
          </div>
          <div className="flex items-center space-x-4">
            <span>
              Total Rows: {(() => {
                let totalRows = 0
                for (const table of tables) {
                  const rows = typeof table.rows === 'number' ? table.rows : 0
                  totalRows += rows
                }
                return totalRows.toLocaleString()
              })()}
            </span>
            <span>
              Total Size: {(() => {
                let totalSize = 0
                for (const table of tables) {
                  totalSize += parseSizeToBytes(table.size)
                }
                return formatSize(totalSize)
              })()}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
